import { createFileRoute } from '@tanstack/react-router';
import {
  type VisibilityState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { Row } from '@tanstack/react-table';
import { zodValidator } from '@tanstack/zod-adapter';
import { type ChangeEvent, useCallback, useMemo, useState } from 'react';

import {
  createFilterDefHelper,
  useComposableFilter,
} from '@/components/ComposableFilter';
import {
  DataTable,
  DataTablePagination,
  useTableDisplayState,
} from '@/components/DataTable';
import { PlaceFilterChip } from '@/components/FilterChip';
import { FillScreen, HStack } from '@/components/Layout';
import SearchInput from '@/components/SearchInput';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { usePaginationSearch, useSortingSearch } from '@/lib/listPage';

import { BoardTypeFilterChip } from './-components/BoardTypeFilterChip';
import {
  columnToSortColumn,
  sortColumnToColumn,
  useBoardMemberColumns,
} from './-components/columns';
import { useBoardMembersQuery } from './-query';
import type { BoardMemberTableRow } from './-types';
import { SearchSchema } from './-zod-schema';

type BoardMemberFilter = {
  boardMemberTypeIdIn?: string[];
  placeIdIn?: string[];
};

const helper = createFilterDefHelper<BoardMemberFilter>();
const filterDefs = [
  helper('boardMemberTypeIdIn', { component: BoardTypeFilterChip }),
  helper('placeIdIn', { component: PlaceFilterChip }),
];

export const Route = createFileRoute(
  '/_staff/settings/contracting-corporation-profile/_layout/board-member/',
)({
  component: BoardMemberList,
  validateSearch: zodValidator(SearchSchema),
});

function BoardMemberList() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.contractingCorporationProfile.boardMember',
  });

  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const [pagination, setPagination] = usePaginationSearch({
    search: search,
    onSearchChange: (search) => navigate({ search }),
  });

  const [sorting, setSorting] = useSortingSearch({
    search,
    onSearchChange: (newSearch) => {
      // テーブルカラムIDをAPIソートカラム名に変換
      const sortColumn = newSearch.sortColumn
        ? (columnToSortColumn[newSearch.sortColumn] ?? newSearch.sortColumn)
        : undefined;
      navigate({
        search: {
          ...newSearch,
          sortColumn: sortColumn as SearchSchema['sortColumn'],
        },
      });
    },
    // APIソートカラム名をテーブルカラムIDに変換（表示用）
    convertSortColumn: (sortColumn) =>
      sortColumnToColumn[sortColumn] ?? sortColumn,
  });

  const { data, isPending } = useBoardMembersQuery({
    freeText: search.freeText,
    boardMemberTypeIdIn: search.boardMemberTypeIdIn,
    placeIdIn: search.placeIdIn,
    page: search.pageIndex + 1,
    pageSize: search.pageSize,
    orderBy: search.sortColumn,
    orderDirection: search.sortOrder,
  });

  const columns = useBoardMemberColumns();

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  // oxlint-disable-next-line react-hooks-js/incompatible-library -- Compiler 非互換のサードパーティ API。TODO: eslint-plugin-react-hooks v7 の React Compiler ルールに対応
  const table = useReactTable({
    data: data?.boardMembers ?? [],
    pageCount: data?.maxPage ?? 0,
    columns: columns,
    initialState: {
      columnPinning: { left: ['person'], right: ['actions'] },
    },
    state: { pagination, sorting, columnVisibility },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    manualPagination: true,
    manualSorting: true,
  });

  const displayState = useTableDisplayState(
    isPending,
    data?.boardMembers ?? [],
  );

  const handleRowClick = useCallback(
    (row: Row<BoardMemberTableRow>) => {
      navigate({
        to: '/person/$id',
        params: { id: row.original.person.id },
      });
    },
    [navigate],
  );

  const handleFilterInput = useDebouncedCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      navigate({
        search: {
          ...search,
          freeText: e.target.value || undefined,
          pageIndex: 0,
        },
      });
    },
    [],
  );

  const handleFilterStateChange = useCallback(
    (
      state:
        | BoardMemberFilter
        | ((prev: BoardMemberFilter) => BoardMemberFilter),
    ) => {
      const newState =
        typeof state === 'function'
          ? state({
              boardMemberTypeIdIn: search.boardMemberTypeIdIn,
              placeIdIn: search.placeIdIn,
            })
          : state;
      navigate({
        search: {
          ...search,
          ...newState,
          pageIndex: 0,
        },
      });
    },
    [navigate, search],
  );

  const filterState = useMemo(
    () => ({
      boardMemberTypeIdIn: search.boardMemberTypeIdIn,
      placeIdIn: search.placeIdIn,
    }),
    [search.boardMemberTypeIdIn, search.placeIdIn],
  );

  const filter = useComposableFilter<BoardMemberFilter>({
    filterDefs,
    filterState,
    onFilterStateChange: handleFilterStateChange,
  });

  return (
    <FillScreen className="flex flex-col gap-4 px-12 py-5">
      <HStack className="justify-between items-center p-2 rounded bg-background">
        <HStack className="gap-2">
          <SearchInput
            className="w-56 h-8 shrink-0"
            placeholder={t('searchPlaceholder')}
            defaultValue={search.freeText}
            onChange={handleFilterInput}
          />
          {filter.getAllFilterDefs().map(({ component: Component, ...def }) => (
            <Component
              key={def.id}
              value={filter.filterState[def.id]}
              onChange={filter.getOnChangeHandler(def.id)}
            />
          ))}
        </HStack>
      </HStack>
      <div className="overflow-auto flex-1 min-h-0">
        <DataTable
          table={table}
          displayState={displayState}
          onRowClick={handleRowClick}
          isHoverable
        />
      </div>
      <DataTablePagination table={table} totalCount={data?.total} />
    </FillScreen>
  );
}
